import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpotlightStays } from '../hooks/useStays';
import ApiDebugger from './ApiDebugger';
import ResponsiveImage from './ResponsiveImage';

const StaysInSpotlight = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  // Stable callback functions to prevent infinite loops
  const handleSuccess = useCallback((data) => {
    console.log('✅ Spotlight stays loaded:', data);
  }, []);

  const handleError = useCallback((error) => {
    console.error('❌ Failed to load spotlight stays:', error);
  }, []);

  // Fetch spotlight stays using our custom hook
  const { 
    data: stays, 
    loading,
    isLoading,
    error, 
    isError, 
    refresh 
  } = useSpotlightStays({
    onSuccess: handleSuccess,
    onError: handleError
  });

  const visibleStays = stays || [];

  // Debug logging to prevent infinite loops
  useEffect(() => {
    console.log('🔄 StaysInSpotlight render - stays count:', stays?.length || 0);
  }, [stays?.length]);

  const scroll = useCallback((direction) => {
    if (!visibleStays || visibleStays.length === 0) return;
    
    const container = scrollRef.current;
    const scrollAmount = 300;
    
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      setCurrentSlide(Math.max(0, currentSlide - 1));
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setCurrentSlide(Math.min(visibleStays.length - 1, currentSlide + 1));
    }
  }, [visibleStays, currentSlide]);

  const handleStayClick = useCallback((stayId) => {
    navigate(`/property/${stayId}`);
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <>
        <section className="py-[32px] md:py-[60px]">
          <div className="px-[0px] md:px-[60px] lg:px-[120px]">
            <div className="flex items-center justify-between mb-[20px]">
              <div className="flex px-[16px] items-center">
                <h2 className="text-[20px] font-medium text-black">Stays in Spotlight</h2>
                <span className="flex items-center space-x-[3px]">
                  {/* House Icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path d="M5 12.1396H3L12 3.13965L21 12.1396H19" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5 12.1396V19.1396C5 19.6701 5.21071 20.1788 5.58579 20.5539C5.96086 20.9289 6.46957 21.1396 7 21.1396H17C17.5304 21.1396 18.0391 20.9289 18.4142 20.5539C18.7893 20.1788 19 19.6701 19 19.1396V12.1396" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9 21.1396V15.1396C9 14.6092 9.21071 14.1005 9.58579 13.7254C9.96086 13.3504 10.4696 13.1396 11 13.1396H13C13.5304 13.1396 14.0391 13.3504 14.4142 13.7254C14.7893 14.1005 15 14.6092 15 15.1396V21.1396" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {/* Bone Icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clip-path="url(#clip0_174_2237)">
                      <path d="M14.9999 3.13965C15.7955 3.13965 16.5586 3.45572 17.1212 4.01833C17.6838 4.58094 17.9999 5.344 17.9999 6.13965C18.4932 6.13954 18.979 6.2611 19.4141 6.49356C19.8493 6.72602 20.2204 7.06221 20.4945 7.47234C20.7687 7.88247 20.9375 8.35389 20.986 8.84484C21.0345 9.33579 20.9612 9.83112 20.7725 10.287C20.5838 10.7428 20.2856 11.1451 19.9044 11.4581C19.5231 11.7712 19.0705 11.9854 18.5867 12.0818C18.1028 12.1782 17.6027 12.1538 17.1306 12.0107C16.6584 11.8677 16.2289 11.6104 15.8799 11.2616L11.1219 16.0196C11.4653 16.3633 11.7201 16.7853 11.8644 17.2492C12.0086 17.7131 12.0381 18.2051 11.9502 18.6829C11.8623 19.1607 11.6596 19.61 11.3597 19.9922C11.0598 20.3744 10.6715 20.678 10.2283 20.877C9.78506 21.0759 9.30015 21.1643 8.81524 21.1344C8.33033 21.1045 7.85993 20.9573 7.44448 20.7055C7.02903 20.4536 6.68096 20.1046 6.4302 19.6885C6.17944 19.2724 6.03349 18.8016 6.0049 18.3166V18.1396H5.8289C5.34373 18.1112 4.87274 17.9653 4.45643 17.7146C4.04013 17.4638 3.69098 17.1157 3.43903 16.7001C3.18707 16.2845 3.03986 15.8139 3.01005 15.3288C2.98023 14.8437 3.06871 14.3587 3.26788 13.9154C3.46705 13.472 3.77093 13.0838 4.15339 12.7839C4.53586 12.484 4.98544 12.2816 5.46347 12.194C5.94151 12.1063 6.43367 12.1361 6.89763 12.2808C7.36158 12.4256 7.78344 12.6808 8.1269 13.0246L12.8849 8.26665C12.4652 7.84721 12.1793 7.31272 12.0634 6.73078C11.9474 6.14885 12.0067 5.5456 12.2336 4.99734C12.4605 4.44907 12.8449 3.98042 13.3382 3.65064C13.8315 3.32086 14.4115 3.14478 15.0049 3.14465L14.9999 3.13965Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_174_2237">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.139648)"/>
                      </clipPath>
                    </defs>
                  </svg>
                </span>
              </div>
            </div>
            
            {/* Loading skeleton */}
            <div className="flex gap-4 overflow-hidden">
              {[1, 2, 3].map((item) => (
                <div key={item} className="flex-shrink-0 w-[290px] bg-gray-200 rounded-[20px] animate-pulse">
                  <div className="h-[300px] bg-gray-300 rounded-t-[20px]"></div>
                  <div className="p-[12px]">
                    <div className="h-5 bg-gray-300 rounded mb-2"></div>
                    <div className="h-3 bg-gray-300 rounded w-20"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <ApiDebugger 
          apiCalls={0} 
          lastCall={null} 
          error={null}
        />
      </>
    );
  }

  // Error state
  if (isError) {
    return (
      <>
        <section className="py-[32px] md:py-[60px]">
          <div className="px-[0px] md:px-[60px] lg:px-[120px]">
            <div className="flex items-center justify-between mb-[20px]">
              <div className="flex  px-[16px] items-center">
                <h2 className="text-[20px] font-medium text-black">Stays in Spotlight</h2>
                <span className="flex items-center space-x-[3px]">
                  {/* House Icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path d="M5 12.1396H3L12 3.13965L21 12.1396H19" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5 12.1396V19.1396C5 19.6701 5.21071 20.1788 5.58579 20.5539C5.96086 20.9289 6.46957 21.1396 7 21.1396H17C17.5304 21.1396 18.0391 20.9289 18.4142 20.5539C18.7893 20.1788 19 19.6701 19 19.1396V12.1396" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9 21.1396V15.1396C9 14.6092 9.21071 14.1005 9.58579 13.7254C9.96086 13.3504 10.4696 13.1396 11 13.1396H13C13.5304 13.1396 14.0391 13.3504 14.4142 13.7254C14.7893 14.1005 15 14.6092 15 15.1396V21.1396" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {/* Bone Icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clip-path="url(#clip0_174_2237)">
                      <path d="M14.9999 3.13965C15.7955 3.13965 16.5586 3.45572 17.1212 4.01833C17.6838 4.58094 17.9999 5.344 17.9999 6.13965C18.4932 6.13954 18.979 6.2611 19.4141 6.49356C19.8493 6.72602 20.2204 7.06221 20.4945 7.47234C20.7687 7.88247 20.9375 8.35389 20.986 8.84484C21.0345 9.33579 20.9612 9.83112 20.7725 10.287C20.5838 10.7428 20.2856 11.1451 19.9044 11.4581C19.5231 11.7712 19.0705 11.9854 18.5867 12.0818C18.1028 12.1782 17.6027 12.1538 17.1306 12.0107C16.6584 11.8677 16.2289 11.6104 15.8799 11.2616L11.1219 16.0196C11.4653 16.3633 11.7201 16.7853 11.8644 17.2492C12.0086 17.7131 12.0381 18.2051 11.9502 18.6829C11.8623 19.1607 11.6596 19.61 11.3597 19.9922C11.0598 20.3744 10.6715 20.678 10.2283 20.877C9.78506 21.0759 9.30015 21.1643 8.81524 21.1344C8.33033 21.1045 7.85993 20.9573 7.44448 20.7055C7.02903 20.4536 6.68096 20.1046 6.4302 19.6885C6.17944 19.2724 6.03349 18.8016 6.0049 18.3166V18.1396H5.8289C5.34373 18.1112 4.87274 17.9653 4.45643 17.7146C4.04013 17.4638 3.69098 17.1157 3.43903 16.7001C3.18707 16.2845 3.03986 15.8139 3.01005 15.3288C2.98023 14.8437 3.06871 14.3587 3.26788 13.9154C3.46705 13.472 3.77093 13.0838 4.15339 12.7839C4.53586 12.484 4.98544 12.2816 5.46347 12.194C5.94151 12.1063 6.43367 12.1361 6.89763 12.2808C7.36158 12.4256 7.78344 12.6808 8.1269 13.0246L12.8849 8.26665C12.4652 7.84721 12.1793 7.31272 12.0634 6.73078C11.9474 6.14885 12.0067 5.5456 12.2336 4.99734C12.4605 4.44907 12.8449 3.98042 13.3382 3.65064C13.8315 3.32086 14.4115 3.14478 15.0049 3.14465L14.9999 3.13965Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_174_2237">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.139648)"/>
                      </clipPath>
                    </defs>
                  </svg>
                </span>
              </div>
            </div>
            
            {/* Error message */}
            <div className="text-center py-8">
              <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-lg font-medium">Failed to load spotlight stays</p>
                <p className="text-sm text-gray-600 mt-1">{error?.message || 'Something went wrong'}</p>
              </div>
              <button 
                onClick={refresh}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </section>
        <ApiDebugger 
          apiCalls={0} 
          lastCall={null} 
          error={error}
        />
      </>
    );
  }

  // Spotlight data exists, but nothing in the selected city
  if (stays && stays.length > 0 && visibleStays.length === 0) {
    return (
      <>
        <section className="py-[32px] md:py-[60px]">
          <div className="px-[0px] md:px-[60px] lg:px-[120px]">
            <div className="flex items-center justify-between mb-[20px]">
              <div className="flex px-[16px] items-center">
                <h2 className="text-[20px] font-medium text-black">Stays in Spotlight</h2>
                <span className="flex items-center space-x-[3px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path d="M5 12.1396H3L12 3.13965L21 12.1396H19" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 12.1396V19.1396C5 19.6701 5.21071 20.1788 5.58579 20.5539C5.96086 20.9289 6.46957 21.1396 7 21.1396H17C17.5304 21.1396 18.0391 20.9289 18.4142 20.5539C18.7893 20.1788 19 19.6701 19 19.1396V12.1396" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 21.1396V15.1396C9 14.6092 9.21071 14.1005 9.58579 13.7254C9.96086 13.3504 10.4696 13.1396 11 13.1396H13C13.5304 13.1396 14.0391 13.3504 14.4142 13.7254C14.7893 14.1005 15 14.6092 15 15.1396V21.1396" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </div>
            <div className="text-center py-8 px-4">
              <div className="text-gray-500">
                <p className="text-lg font-medium text-gray-800">No spotlight stays yet</p>
                <p className="text-sm text-gray-600 mt-2">Featured stays will appear here once they are marked in spotlight.</p>
              </div>
            </div>
          </div>
        </section>
        <ApiDebugger apiCalls={0} lastCall={null} error={null} />
      </>
    );
  }

  // No stays found
  if (!stays || stays.length === 0) {
    return (
      <>
        <section className="py-[32px] md:py-[60px]">
          <div className="px-[0px] md:px-[60px] lg:px-[120px]">
            <div className="flex items-center justify-between mb-[20px]">
              <div className="flex px-[16px] items-center">
                <h2 className="text-[20px] font-medium text-black">Stays in Spotlight</h2>
                <span className="flex items-center space-x-[3px]">
                  {/* House Icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path d="M5 12.1396H3L12 3.13965L21 12.1396H19" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 12.1396V19.1396C5 19.6701 5.21071 20.1788 5.58579 20.5539C5.96086 20.9289 6.46957 21.1396 7 21.1396H17C17.5304 21.1396 18.0391 20.9289 18.4142 20.5539C18.7893 20.1788 19 19.6701 19 19.1396V12.1396" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 21.1396V15.1396C9 14.6092 9.21071 14.1005 9.58579 13.7254C9.96086 13.3504 10.4696 13.1396 11 13.1396H13C13.5304 13.1396 14.0391 13.3504 14.4142 13.7254C14.7893 14.1005 15 14.6092 15 15.1396V21.1396" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {/* Bone Icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <g clip-path="url(#clip0_174_2237)">
                      <path d="M14.9999 3.13965C15.7955 3.13965 16.5586 3.45572 17.1212 4.01833C17.6838 4.58094 17.9999 5.344 17.9999 6.13965C18.4932 6.13954 18.979 6.2611 19.4141 6.49356C19.8493 6.72602 20.2204 7.06221 20.4945 7.47234C20.7687 7.88247 20.9375 8.35389 20.986 8.84484C21.0345 9.33579 20.9612 9.83112 20.7725 10.287C20.5838 10.7428 20.2856 11.1451 19.9044 11.4581C19.5231 11.7712 19.0705 11.9854 18.5867 12.0818C18.1028 12.1782 17.6027 12.1538 17.1306 12.0107C16.6584 11.8677 16.2289 11.6104 15.8799 11.2616L11.1219 16.0196C11.4653 16.3633 11.7201 16.7853 11.8644 17.2492C12.0086 17.7131 12.0381 18.2051 11.9502 18.6829C11.8623 19.1607 11.6596 19.61 11.3597 19.9922C11.0598 20.3744 10.6715 20.678 10.2283 20.877C9.78506 21.0759 9.30015 21.1643 8.81524 21.1344C8.33033 21.1045 7.85993 20.9573 7.44448 20.7055C7.02903 20.4536 6.68096 20.1046 6.4302 19.6885C6.17944 19.2724 6.03349 18.8016 6.0049 18.3166V18.1396H5.8289C5.34373 18.1112 4.87274 17.9653 4.45643 17.7146C4.04013 17.4638 3.69098 17.1157 3.43903 16.7001C3.18707 16.2845 3.03986 15.8139 3.01005 15.3288C2.98023 14.8437 3.06871 14.3587 3.26788 13.9154C3.46705 13.472 3.77093 13.0838 4.15339 12.7839C4.53586 12.484 4.98544 12.2816 5.46347 12.194C5.94151 12.1063 6.43367 12.1361 6.89763 12.2808C7.36158 12.4256 7.78344 12.6808 8.1269 13.0246L12.8849 8.26665C12.4652 7.84721 12.1793 7.31272 12.0634 6.73078C11.9474 6.14885 12.0067 5.5456 12.2336 4.99734C12.4605 4.44907 12.8449 3.98042 13.3382 3.65064C13.8315 3.32086 14.4115 3.14478 15.0049 3.14465L14.9999 3.13965Z" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_174_2237">
                        <rect width="24" height="24" fill="white" transform="translate(0 0.139648)"/>
                      </clipPath>
                    </defs>
                  </svg>
                </span>
              </div>
            </div>
            
            {/* No stays message */}
            <div className="text-center py-8">
              <div className="text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p className="text-lg font-medium">No spotlight stays found</p>
                <p className="text-sm text-gray-600 mt-1">Check back later for featured properties</p>
              </div>
            </div>
          </div>
        </section>
        <ApiDebugger 
          apiCalls={0} 
          lastCall={null} 
          error={null}
        />
      </>
    );
  }

  return (
    <>
    <section className="py-[32px] md:py-[60px]">
      <div className="px-[0px] md:px-[60px] lg:px-[120px]">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-[20px]">
          <div className="flex  px-[16px] items-center">
            <h2 className="text-[20px] font-medium text-black">Stays in Spotlight</h2>
            <span className="flex items-center space-x-[3px]">
              {/* House Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                <path d="M5 12.1396H3L12 3.13965L21 12.1396H19" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M5 12.1396V19.1396C5 19.6701 5.21071 20.1788 5.58579 20.5539C5.96086 20.9289 6.46957 21.1396 7 21.1396H17C17.5304 21.1396 18.0391 20.9289 18.4142 20.5539C18.7893 20.1788 19 19.6701 19 19.1396V12.1396" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9 21.1396V15.1396C9 14.6092 9.21071 14.1005 9.58579 13.7254C9.96086 13.3504 10.4696 13.1396 11 13.1396H13C13.5304 13.1396 14.0391 13.3504 14.4142 13.7254C14.7893 14.1005 15 14.6092 15 15.1396V21.1396" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              {/* Bone Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                <g clip-path="url(#clip0_174_2237)">
                    <path d="M14.9999 3.13965C15.7955 3.13965 16.5586 3.45572 17.1212 4.01833C17.6838 4.58094 17.9999 5.344 17.9999 6.13965C18.4932 6.13954 18.979 6.2611 19.4141 6.49356C19.8493 6.72602 20.2204 7.06221 20.4945 7.47234C20.7687 7.88247 20.9375 8.35389 20.986 8.84484C21.0345 9.33579 20.9612 9.83112 20.7725 10.287C20.5838 10.7428 20.2856 11.1451 19.9044 11.4581C19.5231 11.7712 19.0705 11.9854 18.5867 12.0818C18.1028 12.1782 17.6027 12.1538 17.1306 12.0107C16.6584 11.8677 16.2289 11.6104 15.8799 11.2616L11.1219 16.0196C11.4653 16.3633 11.7201 16.7853 11.8644 17.2492C12.0086 17.7131 12.0381 18.2051 11.9502 18.6829C11.8623 19.1607 11.6596 19.61 11.3597 19.9922C11.0598 20.3744 10.6715 20.678 10.2283 20.877C9.78506 21.0759 9.30015 21.1643 8.81524 21.1344C8.33033 21.1045 7.85993 20.9573 7.44448 20.7055C7.02903 20.4536 6.68096 20.1046 6.4302 19.6885C6.17944 19.2724 6.03349 18.8016 6.0049 18.3166V18.1396H5.8289C5.34373 18.1112 4.87274 17.9653 4.45643 17.7146C4.04013 17.4638 3.69098 17.1157 3.43903 16.7001C3.18707 16.2845 3.03986 15.8139 3.01005 15.3288C2.98023 14.8437 3.06871 14.3587 3.26788 13.9154C3.46705 13.472 3.77093 13.0838 4.15339 12.7839C4.53586 12.484 4.98544 12.2816 5.46347 12.194C5.94151 12.1063 6.43367 12.1361 6.89763 12.2808C7.36158 12.4256 7.78344 12.6808 8.1269 13.0246L12.8849 8.26665C12.4652 7.84721 12.1793 7.31272 12.0634 6.73078C11.9474 6.14885 12.0067 5.5456 12.2336 4.99734C12.4605 4.44907 12.8449 3.98042 13.3382 3.65064C13.8315 3.32086 14.4115 3.14478 15.0049 3.14465L14.9999 3.13965Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                    <clipPath id="clip0_174_2237">
                    <rect width="24" height="24" fill="white" transform="translate(0 0.139648)"/>
                    </clipPath>
                </defs>
                </svg>
            </span>
          </div>
          <button
            type="button"
            onClick={() => navigate('/search-results')}
            className="text-blue-600 text-sm font-normal underline shrink-0 px-[16px] md:pr-0 hover:opacity-80"
          >
            View All
          </button>
        </div>

        {/* Horizontal Scrolling Container */}
        <div className="relative">
          <div 
            ref={scrollRef}
            className="flex gap-[8px] px-[16px] overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {visibleStays.map((stay) => (
              <div 
                  key={stay.stayId}
                className="flex-shrink-0 w-[290px] bg-white rounded-[20px] overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-300"
                  onClick={() => handleStayClick(stay.stayId)}
              >
                  {/* Image - Use first image from images array */}
                <div className="relative h-[300px]">
                  <ResponsiveImage
                    src={
                      stay.images && stay.images.length > 0
                        ? stay.images[0]
                        : 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800'
                    }
                    variant="spotlightCard"
                    alt={stay.title}
                    className="h-full w-full object-cover object-center"
                    onError={(e) => {
                      const fallback =
                        'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800';
                      if (e.target.src !== fallback) {
                        e.target.src = fallback;
                      }
                    }}
                  />
                </div>
                
                {/* Content */}
                <div className="p-[12px]">
                  <h3 className="font-medium text-gray-900 text-[20px] mb-[4px] leading-[1.1]">
                    {stay.title}
                  </h3>
                    <p className="text-[#bebebe] text-[12px] mb-2">{stay.city}, {stay.state}</p>
                    
                   
                </div>
              </div>
            ))}
          </div>
        </div>

         
      </div>
    </section>
      
      {/* API Debugger Component */}
     
    </>
  );
};

export default StaysInSpotlight; 